import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, AnimeCharacter, Rating } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AuthModal from './components/AuthModal';
import RatingForm from './components/RatingForm';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function RatePage() {
  const { user, loading: authLoading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentCharacter, setCurrentCharacter] = useState<AnimeCharacter | null>(null);
  const [userRating, setUserRating] = useState<Rating | null>(null);
  const [genderFilter, setGenderFilter] = useState<'all' | 'boy' | 'girl'>('all');
  const [ratedCharacterIds, setRatedCharacterIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [skippedCharacterIds, setSkippedCharacterIds] = useState<string[]>([]);
  const [ratedCharactersLoaded, setRatedCharactersLoaded] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Helper function to create SEO-friendly slug
  const createCharacterSlug = (character: AnimeCharacter) => {
    const animeName = character.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const charName = character.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animeName}-${charName}-${character.id}`;
  };

  // Show auth modal only after auth loading is complete and user is not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      setShowAuthModal(true);
    }
  }, [user, authLoading]);

  // Load rated characters first
  useEffect(() => {
    const loadRatedCharactersOnInit = async () => {
      if (!authLoading) {
        if (user) {
          await loadRatedCharacters();
        }
        setRatedCharactersLoaded(true);
      }
    };
    
    loadRatedCharactersOnInit();
  }, [authLoading, user]);

  // Load random character only after rated characters are loaded
  useEffect(() => {
    if (ratedCharactersLoaded && isInitialLoad) {
      loadRandomCharacter();
      setIsInitialLoad(false);
    }
  }, [ratedCharactersLoaded]);

  // Only reload when gender filter changes (not on initial load)
  useEffect(() => {
    if (!isInitialLoad && ratedCharactersLoaded) {
      loadRandomCharacter();
    }
  }, [genderFilter]);

  // Reset image loading state when character changes
  useEffect(() => {
    if (currentCharacter) {
      setImageLoading(true);
      setImageLoaded(false);
    }
  }, [currentCharacter?.id]);

  const loadRatedCharacters = async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from('ratings')
      .select('character_id')
      .eq('user_id', user.id);
    
    if (data) {
      setRatedCharacterIds(data.map(r => r.character_id));
    }
  };

  const loadRandomCharacter = async () => {
    setLoading(true);
    setImageLoading(true);
    setImageLoaded(false);
    
    let query = supabase
      .from('anime_characters')
      .select('*');
    
    if (genderFilter !== 'all') {
      // Include neutral gender in both Girls Only and Boys Only filters
      if (genderFilter === 'girl') {
        query = query.in('gender', ['girl', 'neutral']);
      } else if (genderFilter === 'boy') {
        query = query.in('gender', ['boy', 'neutral']);
      }
    }
    
    const { data: allCharacters } = await query;
    
    if (allCharacters && allCharacters.length > 0) {
      // Exclude current character and recently skipped characters
      const excludeIds = [
        ...(currentCharacter ? [currentCharacter.id] : []),
        ...skippedCharacterIds
      ];
      
      // First, try to find unrated characters that haven't been skipped
      let availableCharacters = allCharacters.filter(
        c => !ratedCharacterIds.includes(c.id) && !excludeIds.includes(c.id)
      );
      
      // If no unrated characters available, use all characters except excluded ones
      if (availableCharacters.length === 0) {
        availableCharacters = allCharacters.filter(
          c => !excludeIds.includes(c.id)
        );
      }
      
      // If still no characters (all excluded), reset and use all characters
      if (availableCharacters.length === 0) {
        availableCharacters = allCharacters.filter(
          c => currentCharacter ? c.id !== currentCharacter.id : true
        );
        // Clear skipped list when we've exhausted all options
        setSkippedCharacterIds([]);
      }
      
      // Pick a random character from available ones
      const selectedCharacter = availableCharacters[
        Math.floor(Math.random() * availableCharacters.length)
      ];
      
      setCurrentCharacter(selectedCharacter);
      
      if (user) {
        const { data: rating } = await supabase
          .from('ratings')
          .select('*')
          .eq('user_id', user.id)
          .eq('character_id', selectedCharacter.id)
          .single();
        
        setUserRating(rating);
      }
    }
    
    setLoading(false);
  };

  const handleRatingSubmit = async (rating: number) => {
    if (!user || !currentCharacter) return;
    
    try {
      if (userRating) {
        const { error } = await supabase
          .from('ratings')
          .update({ rating, updated_at: new Date().toISOString() })
          .eq('id', userRating.id);
        
        if (error) {
          console.error('Error updating rating:', error);
          alert('Failed to update rating. Please try again.');
          return;
        }
      } else {
        const { error } = await supabase
          .from('ratings')
          .insert({
            user_id: user.id,
            character_id: currentCharacter.id,
            rating,
          });
        
        if (error) {
          console.error('Error inserting rating:', error);
          alert('Failed to save rating. Please try again.');
          return;
        }
        
        // Add the character to rated list immediately after successful rating
        setRatedCharacterIds(prev => [...prev, currentCharacter.id]);
      }
      
      // Clear skipped list after rating
      setSkippedCharacterIds([]);
      
      // Load next character after successful rating
      loadRandomCharacter();
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('An unexpected error occurred. Please try again.');
    }
  };

  const handleSkip = () => {
    // Add current character to skipped list
    if (currentCharacter) {
      setSkippedCharacterIds(prev => {
        const newList = [...prev, currentCharacter.id];
        // Keep only last 20 skipped characters to prevent memory issues
        return newList.slice(-20);
      });
    }
    loadRandomCharacter();
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageLoaded(false);
  };

  const jsonLd = currentCharacter ? {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Rate Anime Characters',
    description: 'Rate anime characters from 1 to 10 and share your opinion with the community',
    url: `${siteUrl}/rate`,
    mainEntity: {
      '@type': 'Person',
      name: currentCharacter.character_name,
      image: currentCharacter.image_url,
      description: `${currentCharacter.character_name} from ${currentCharacter.anime_name}`,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: currentCharacter.average_rating.toFixed(1),
        bestRating: '10',
        worstRating: '1',
        ratingCount: currentCharacter.total_ratings
      }
    }
  } : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="Rate Anime Characters - Anime HOT or NOT"
        description="Rate your favorite anime characters from 1 to 10. Discover the hottest anime characters and share your opinions with our community of anime fans."
        keywords="rate anime characters, anime rating, character rating, anime hot or not, vote anime characters, anime community"
        canonical={`${siteUrl}/rate`}
        jsonLd={jsonLd}
      />
      
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setGenderFilter('all')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  genderFilter === 'all'
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                All Characters
              </button>
              <button
                onClick={() => setGenderFilter('girl')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  genderFilter === 'girl'
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                Girls Only
              </button>
              <button
                onClick={() => setGenderFilter('boy')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  genderFilter === 'boy'
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                Boys Only
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
            </div>
          ) : currentCharacter ? (
            <>
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8 pb-6">
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {currentCharacter.character_name}
                  </h1>
                  <Link
                    to={`/series/${encodeURIComponent(currentCharacter.anime_name)}`}
                    className="text-lg text-gray-600 hover:text-pink-500 transition-colors cursor-pointer inline-block mb-6"
                  >
                    from {currentCharacter.anime_name}
                  </Link>
                  
                  <div className="flex items-center gap-6 mb-0 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-heart-fill text-pink-500"></i>
                      <span>
                        <strong>{currentCharacter.average_rating.toFixed(1)}</strong> average
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-user-line text-gray-500"></i>
                      <span>
                        <strong>{currentCharacter.total_ratings}</strong> ratings
                      </span>
                    </div>
                  </div>
                </div>

                <div className="aspect-[3/4] relative bg-gray-100">
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
                    </div>
                  )}
                  <img
                    key={currentCharacter.id}
                    src={currentCharacter.image_url}
                    alt={currentCharacter.character_name}
                    className={`w-full h-full object-cover object-top transition-opacity duration-300 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                </div>
                
                <div className="p-8">
                  {user ? (
                    <RatingForm
                      onRate={handleRatingSubmit}
                      onSkip={handleSkip}
                      existingRating={userRating?.rating}
                      isUpdate={!!userRating}
                      characterName={currentCharacter?.character_name}
                      animeName={currentCharacter?.anime_name}
                    />
                  ) : (
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="w-full py-4 text-lg font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                    >
                      Login to Rate
                    </button>
                  )}
                </div>
              </div>

              {currentCharacter.description && currentCharacter.description.trim() && (
                <div className="bg-white rounded-2xl shadow-xl p-8 mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">About {currentCharacter.character_name}</h2>
                  <div 
                    className="prose prose-sm max-w-none text-gray-700 character-description-content"
                    dangerouslySetInnerHTML={{ __html: currentCharacter.description }}
                    style={{
                      lineHeight: '1.8'
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-lg text-gray-600">No characters available to rate.</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
      
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}

      <style>{`
        .character-description-content p {
          margin-bottom: 1.5em;
        }
        .character-description-content p:last-child {
          margin-bottom: 0;
        }
        .character-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .character-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
